import React, { useEffect, useState } from 'react';
import {
  Map,
  Satellite,
  Route,
  Radar,
  Smartphone,
  ShieldCheck,
  Clock,
  Activity,
  BarChart3,
  Bell,
  Globe,
  Waves,
  Camera,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const liveFeatures = [
  {
    icon: Map,
    title: 'Real-Time Positioning',
    description:
      'Get second-by-second GPS updates with high precision geofencing, trip timelines, and multi-vehicle map views.',
    image:
      'https://plus.unsplash.com/premium_photo-1745056197458-1f2b490a3493?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bW9iaWxlJTIwbWFwc3xlbnwwfHwwfHx8MA%3D%3D',
  },
  {
    icon: Radar,
    title: 'Sensor Fusion Telemetry',
    description:
      'Combine GPS, CAN, and OBD data to monitor fuel, engine health, tyre pressure, and driver behaviour in one dashboard.',
    image:
      'https://images.unsplash.com/photo-1599508266124-804fc6eecf09?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8c2Vuc29yc3xlbnwwfHwwfHx8MA%3D%3D',
  },
  {
    icon: Bell,
    title: 'Smart Alerts & Automation',
    description:
      'Configure rule-based alerts for idling, unscheduled stoppages, harsh driving, and route deviations delivered instantly.',
    image:
      'https://images.unsplash.com/photo-1606495813362-8efff01b8573?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8c21hcnQlMjBhbGVydHN8ZW58MHx8MHx8fDA%3D',
  },
];

const capabilitySections = [
  {
    title: 'Command Centre Dashboard',
    description:
      'Monitor your entire fleet with a multi-layered command centre that visualises live locations, routes, and health indicators with drill-down views.',
    points: [
      '3D map overlays with traffic, weather, and geofencing layers',
      'Fleet clustering with heatmaps to identify high-activity zones',
      'Replay any trip with timeline scrubbing and event markers',
    ],
    icon: Satellite,
  },
  {
    title: 'Driver Experience Suite',
    description:
      'Empower drivers with a dedicated companion app that enables navigation, check-ins, SOS triggers, and direct communication with dispatch.',
    points: [
      'Turn-by-turn navigation optimised for vehicle type and constraints',
      'Driver scorecards with AI-generated coaching tips',
      'Offline mode with store-and-forward when connectivity drops',
    ],
    icon: Smartphone,
  },
  {
    title: 'Control & Compliance Engine',
    description:
      'Ensure complete compliance with configurable policies, digital documentation, and automated audit-ready reports.',
    points: [
      'Hours-of-service logs, maintenance reminders, and inspection checklists',
      'Automated compliance exports for RTO, AIS-140, and DPDP regulations',
      'Role-based access control with detailed audit trails',
    ],
    icon: ShieldCheck,
  },
];

const analyticsHighlights = [
  {
    title: 'Predictive ETA Accuracy',
    value: '98%',
    detail: 'Machine learning models factor in historic traffic, weather, and driver behaviour.',
  },
  {
    title: 'Idle Time Reduction',
    value: '32%',
    detail: 'Route optimisation and automated alerts reduce fuel burn across large fleets.',
  },
  {
    title: 'Compliance Automation',
    value: '100%',
    detail: 'Digital trip sheets and tamper-proof logs eliminate manual paperwork.',
  },
];

const faqs = [
  {
    question: 'Does TrackYourWheelz support AIS-140 compliant devices?',
    answer:
      'Yes. We support AIS-140, OBD-II, CAN, and proprietary OEM devices. Our ingestion layer normalises data across device protocols.',
  },
  {
    question: 'Can I integrate the tracker with our ERP or TMS?',
    answer:
      'Absolutely. REST and WebSocket APIs, plus prebuilt connectors for SAP, Oracle, and leading TMS platforms allow seamless data exchange.',
  },
  {
    question: 'How accurate is the GPS tracking?',
    answer:
      'We provide sub 5-meter accuracy under open skies and leverage multi-constellation GNSS with assisted GPS for dense urban conditions.',
  },
  {
    question: 'Is historical data archived?',
    answer:
      'Yes. We retain detailed trip history, telemetry, and alerts for up to 18 months by default with options to extend as needed.',
  },
];

const GpsTrackerPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <div
      className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
        theme === 'dark'
          ? 'bg-gradient-to-r from-[#0f1729] via-[#1f2454] to-[#2d1b4e] text-slate-100'
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
      }`}
    >
      <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="GPS Tracker" />

      <div className="absolute inset-0 pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute -top-20 -left-20 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/3 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-[150px] animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] bg-blue-500/20 rounded-full blur-[180px] animate-pulse delay-500" />
          </>
        ) : (
          <>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
            <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
            <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
          </>
        )}
      </div>

      <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <header className="space-y-5 text-center">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${
                theme === 'dark'
                  ? 'bg-slate-900 border-1 border-muted-foreground/20 text-blue-200'
                  : 'bg-white border-1 border-muted-foreground/20 text-blue-700'
              }`}
            >
              <Map className="h-4 w-4" />
              GPS Tracking Suite
            </div>
            <h1 className="text-sm sm:text-4xl md:text-5xl font-bold leading-tight">
              Command Your Fleet with Precision Tracking Intelligence
            </h1>
            <p
              className={`text-sm sm:text-lg md:text-xl max-w-3xl mx-auto ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz blends military-grade GPS, AI-driven analytics, and intuitive driver
              experiences to deliver the most comprehensive telematics platform for modern fleets.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://trackyourwheelz.com/demo', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                Book a live demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
                onClick={() => window.open('mailto:sales@trackyourwheelz.com', '_blank')}
              >
                Talk to an expert
              </Button>
            </div>
          </header>

          <section className="grid gap-6 md:grid-cols-3">
            {analyticsHighlights.map((highlight) => (
              <Card
                key={highlight.title}
                className={`border-1 border-muted-foreground/20 ${
                  theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                } shadow-lg`}
              >
                <CardHeader className="space-y-2">
                  <CardTitle className="text-sm sm:text-base font-semibold">
                    {highlight.title}
                  </CardTitle>
                  <CardDescription
                    className={`text-3xl sm:text-4xl font-bold ${
                      theme === 'dark' ? 'text-blue-200' : 'text-blue-600'
                    }`}
                  >
                    {highlight.value}
                  </CardDescription>
                </CardHeader>
                <CardContent
                  className={`text-sm leading-relaxed ${
                    theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                  }`}
                >
                  {highlight.detail}
                </CardContent>
              </Card>
            ))}
          </section>

          <section className="space-y-6">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Live Operations Hub</h2>
            <p
              className={`text-sm sm:text-base max-w-3xl mx-auto text-center ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              A unified control tower lets operations teams see every asset, every route, and every
              exception in real time with rich contextual data and automation.
            </p>
            <div className="grid gap-6 lg:grid-cols-3">
              {liveFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className={`overflow-hidden border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <div className="relative h-40">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src =
                            'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&auto=format&fit=crop&q=60';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 text-white">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-semibold">{feature.title}</span>
                      </div>
                    </div>
                    <CardContent
                      className={`p-5 text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {feature.description}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="space-y-8">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Enterprise Capabilities</h2>
            <div className="space-y-6">
              {capabilitySections.map((section) => {
                const Icon = section.icon;
                return (
                  <Card
                    key={section.title}
                    className={`border-1 border-muted-foreground/20 ${
                      theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                    } shadow-lg`}
                  >
                    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
                      <div
                        className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                          theme === 'dark'
                            ? 'bg-blue-500/20 text-blue-200'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-2">
                        <CardTitle className="text-sm sm:text-lg font-semibold">
                          {section.title}
                        </CardTitle>
                        <CardDescription
                          className={`text-sm ${
                            theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                          }`}
                        >
                          {section.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <ul
                        className={`grid gap-3 sm:grid-cols-2 text-sm ${
                          theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      >
                        {section.points.map((point) => (
                          <li key={point} className="flex items-start gap-2">
                            <CheckCircle className="mt-1 h-4 w-4 text-blue-500 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-3">
            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  Time & Route Intelligence
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Automate route planning with live traffic, load capacity, and delivery window
                  constraints to maximise on-time performance.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Dynamic route optimisation reduces empty miles and balances driver schedules with
                  predictive rest stops.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Waves className="mt-1 h-4 w-4 text-blue-500" />
                    Dynamic geofence orchestration with cascading alerts
                  </li>
                  <li className="flex items-start gap-2">
                    <Route className="mt-1 h-4 w-4 text-blue-500" />
                    ETA recalculations and customer notifications in real time
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <Camera className="h-5 w-5 text-blue-500" />
                  Video Telematics Ready
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Integrate ADAS dashcams, cabin cameras, and DVR systems with cloud-based storage
                  and AI analysis.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Detect distracted driving, lane departures, and tailgating with intelligent video
                  analytics and automatically create coaching moments.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Activity className="mt-1 h-4 w-4 text-blue-500" />
                    AI annotations on critical video events
                  </li>
                  <li className="flex items-start gap-2">
                    <ShieldCheck className="mt-1 h-4 w-4 text-blue-500" />
                    Privacy-first storage with granular access control
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card
              className={`border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } shadow-lg`}
            >
              <CardHeader>
                <CardTitle className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Analytics & Integrations
                </CardTitle>
                <CardDescription
                  className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                >
                  Build custom dashboards, export datasets, and push insights to ERPs, TMS, and
                  customer portals.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  Get a complete digital twin of your operations with scheduled reports, anomaly
                  detection, and predictive maintenance signals.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Globe className="mt-1 h-4 w-4 text-blue-500" />
                    REST, GraphQL, and WebSocket APIs with detailed docs
                  </li>
                  <li className="flex items-start gap-2">
                    <Bell className="mt-1 h-4 w-4 text-blue-500" />
                    Plug-and-play integrations with Slack, Teams, and email
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="rounded-3xl overflow-hidden relative">
            <img
              src="https://images.unsplash.com/photo-1602034032025-47f18f22244f?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjI2fHxjYXJ8ZW58MHx8MHx8fDA%3D"
              alt="Fleet control room"
              className="h-72 sm:h-80 w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-y-0 left-0 px-6 sm:px-10 py-8 sm:py-10 flex flex-col justify-between text-white max-w-xl">
              <div className="space-y-3">
                <h2 className="text-sm sm:text-3xl font-bold">
                  Fleet Command Centre on every device
                </h2>
                <p className="text-sm sm:text-base opacity-80">
                  Desktop dashboards, mobile apps, and TV wallboard views keep every stakeholder
                  aligned—from dispatch to management to on-field crews.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
                <span className="rounded-full border border-white/40 px-4 py-1">Native iOS & Android</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Offline Resilience</span>
                <span className="rounded-full border border-white/40 px-4 py-1">Enterprise SSO</span>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-sm sm:text-3xl font-bold text-center">Frequently Asked Questions</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {faqs.map((faq) => (
                <Card
                  key={faq.question}
                  className={`border-1 border-muted-foreground/20 ${
                    theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
                  } shadow-lg`}
                >
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent
                    className={`text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                    }`}
                  >
                    {faq.answer}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <footer
            className={`rounded-2xl border-1 border-muted-foreground/20 p-6 sm:p-8 text-center ${
              theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
            }`}
          >
            <h2 className="text-sm sm:text-3xl font-bold mb-4">Ready to modernise your fleet?</h2>
            <p
              className={`text-sm sm:text-base max-w-2xl mx-auto mb-6 ${
                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              TrackYourWheelz GPS Tracker is trusted by logistics, public transport, mining, and
              emergency response fleets across 150+ cities. We onboard your team in days, not weeks.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => window.open('https://trackyourwheelz.com/signup', '_blank')}
                className={`h-11 px-6 text-sm font-semibold text-white shadow-md ${
                  theme === 'dark'
                    ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                }`}
              >
                Start your pilot
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('mailto:support@trackyourwheelz.com', '_blank')}
                className={`h-11 px-6 text-sm font-semibold ${
                  theme === 'dark'
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : 'border-blue-200 text-blue-700 hover:bg-blue-50'
                }`}
              >
                Contact support
              </Button>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default GpsTrackerPage;